import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { useAdminTermsOfService } from '../hooks/useAdminTermsOfService'
import type { TermsOfServiceSection } from '@/types'

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

interface SortableSectionRowProps {
  section: TermsOfServiceSection
  onEdit: (section: TermsOfServiceSection) => void
  onDelete: (section: TermsOfServiceSection) => void
}

function SortableSectionRow({ section, onEdit, onDelete }: SortableSectionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  })

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <TableCell className="w-8 pr-0">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{section.title}</TableCell>
      <TableCell className="max-w-sm truncate text-sm text-muted-foreground">
        {section.content}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end">
          <MultiButton
            variant="outline"
            size="sm"
            items={[
              { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => onEdit(section) },
              {
                id: 'delete',
                icon: Trash2,
                label: 'Delete',
                onClick: () => onDelete(section),
                className: 'text-destructive hover:text-destructive',
              },
            ]}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

export function AdminTermsOfServicePage() {
  const { sections, lastUpdated, loading, error, saving, setSections, save } =
    useAdminTermsOfService()

  const [localSections, setLocalSections] = useState<TermsOfServiceSection[]>([])

  // Keep local order in sync with hook data
  useEffect(() => {
    setLocalSections(sections)
  }, [sections])

  // ─── Section form state ───────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<TermsOfServiceSection | null>(null)
  const [titleInput, setTitleInput] = useState('')
  const [contentInput, setContentInput] = useState('')
  const [formSaving, setFormSaving] = useState(false)
  const [previewMode, setPreviewMode] = useState(false)

  // ─── Delete dialog state ──────────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<TermsOfServiceSection | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // ─── Drag-and-drop reorder ────────────────────────────────────────────────────
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = localSections.findIndex((s) => s.id === active.id)
    const newIndex = localSections.findIndex((s) => s.id === over.id)
    const reordered = arrayMove(localSections, oldIndex, newIndex)
    setLocalSections(reordered)
    setSections(reordered)
  }

  // ─── Add / edit section ───────────────────────────────────────────────────────
  function openAdd() {
    setEditTarget(null)
    setTitleInput('')
    setContentInput('')
    setPreviewMode(false)
    setFormOpen(true)
  }

  function openEdit(section: TermsOfServiceSection) {
    setEditTarget(section)
    setTitleInput(section.title)
    setContentInput(section.content)
    setPreviewMode(false)
    setFormOpen(true)
  }

  async function handleFormSave() {
    const trimmedTitle = titleInput.trim()
    const trimmedContent = contentInput.trim()
    if (!trimmedTitle || !trimmedContent) return

    setFormSaving(true)
    try {
      if (editTarget) {
        const updated = localSections.map((s) =>
          s.id === editTarget.id ? { ...s, title: trimmedTitle, content: trimmedContent } : s,
        )
        setLocalSections(updated)
        setSections(updated)
      } else {
        const newSection: TermsOfServiceSection = {
          id: generateId(),
          title: trimmedTitle,
          content: trimmedContent,
          order: localSections.length + 1,
        }
        const updated = [...localSections, newSection]
        setLocalSections(updated)
        setSections(updated)
      }
      setFormOpen(false)
    } finally {
      setFormSaving(false)
    }
  }

  // ─── Delete section ───────────────────────────────────────────────────────────
  function handleDelete() {
    if (!deleteTarget) return
    const updated = localSections.filter((s) => s.id !== deleteTarget.id)
    setLocalSections(updated)
    setSections(updated)
    setDeleteOpen(false)
    setDeleteTarget(null)
  }

  // ─── Save to Firestore ────────────────────────────────────────────────────────
  async function handleSave() {
    await save()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Terms of Service</h1>
          {lastUpdated && (
            <p className="text-sm text-muted-foreground mt-0.5">
              Last updated{' '}
              {lastUpdated.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openAdd}>
            <Plus className="h-4 w-4 mr-1" />
            Add Section
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div className="rounded-md border">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Title</TableHead>
                  <TableHead>Content preview</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <MultiButtonGroup>
                <SortableContext
                  items={localSections.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {localSections.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                          No sections yet. Add one to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      localSections.map((section) => (
                        <SortableSectionRow
                          key={section.id}
                          section={section}
                          onEdit={openEdit}
                          onDelete={(s) => { setDeleteTarget(s); setDeleteOpen(true) }}
                        />
                      ))
                    )}
                  </TableBody>
                </SortableContext>
              </MultiButtonGroup>
            </Table>
          </DndContext>
        </div>
      )}

      {/* ─── Add / Edit Dialog ───────────────────────────────────────────────── */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
          <DialogHeader className="shrink-0">
            <DialogTitle>{editTarget ? 'Edit Section' : 'Add Section'}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            <div className="space-y-2">
              <Label htmlFor="tos-section-title">Title</Label>
              <Input
                id="tos-section-title"
                placeholder="e.g. Acceptance of Terms"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Content</Label>
                <div className="flex rounded-md border text-xs overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setPreviewMode(false)}
                    className={`px-3 py-1 transition-colors ${!previewMode ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode(true)}
                    className={`px-3 py-1 border-l transition-colors ${previewMode ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Preview
                  </button>
                </div>
              </div>
              {previewMode ? (
                <div className="min-h-[200px] rounded-md border bg-muted/30 px-3 py-2 prose prose-sm max-w-none text-muted-foreground [&_a]:text-foreground [&_a]:underline [&_strong]:text-foreground">
                  {contentInput.trim() ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {contentInput}
                    </ReactMarkdown>
                  ) : (
                    <p className="text-muted-foreground/50 italic">Nothing to preview.</p>
                  )}
                </div>
              ) : (
                <Textarea
                  id="tos-section-content"
                  placeholder="Markdown is supported — **bold**, _italic_, bullet lists, links, etc."
                  value={contentInput}
                  onChange={(e) => setContentInput(e.target.value)}
                  rows={10}
                  className="resize-y font-mono text-sm"
                />
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleFormSave()}
              disabled={!titleInput.trim() || !contentInput.trim() || formSaving}
            >
              {formSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete Confirmation Dialog ──────────────────────────────────────── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Section</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-foreground">{deleteTarget?.title}</span>?{' '}
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDeleteOpen(false); setDeleteTarget(null) }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
